import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import axios from 'axios';
import {
  Card,
  Button,
  Modal,
  Form,
  Badge,
  Alert,
  Spinner,
  Dropdown,
} from 'react-bootstrap';
import {
  FaClock,
  FaPlus,
  FaEdit,
  FaTrash,
  FaChalkboardTeacher,
  FaBook,
  FaDoorOpen,
  FaCoffee,
  FaExpand,
  FaCompress,
  FaCopy,
  FaTimes,
  FaGripVertical,
} from 'react-icons/fa';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import './ClassScheduler.css';

const getAuthToken = () => localStorage.getItem('authToken');

const parseStoredJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const moveItem = (items, fromIndex, toIndex) => {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
};

const DEFAULT_SESSION_BLOCK_DISPLAYS = ['teacher_code'];

const SESSION_BLOCK_DISPLAY_OPTIONS = [
  { value: 'teacher_code', label: 'Teacher Code' },
  { value: 'subject', label: 'Subject' },
  { value: 'time', label: 'Time' },
  { value: 'type', label: 'Type' },
];

const DEFAULT_CLASS_BLOCK_COLOR = '#2563eb';
const DEFAULT_EXAM_BLOCK_COLOR = '#b45309';
const DEFAULT_BREAK_BLOCK_COLOR = '#64748b';

const getHashHue = (value) => {
  let hash = 0;
  const seed = String(value);

  for (let index = 0; index < seed.length; index += 1) {
    hash = seed.charCodeAt(index) + ((hash << 5) - hash);
  }

  return Math.abs(hash) % 360;
};

const normalizeHexColor = (value) => {
  if (!value) return '';

  const trimmedValue = value.trim();
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmedValue)) {
    return '';
  }

  if (trimmedValue.length === 4) {
    return `#${trimmedValue[1]}${trimmedValue[1]}${trimmedValue[2]}${trimmedValue[2]}${trimmedValue[3]}${trimmedValue[3]}`.toUpperCase();
  }

  return trimmedValue.toUpperCase();
};

const hexToRgb = (hexColor) => {
  const normalizedColor = normalizeHexColor(hexColor);
  if (!normalizedColor) return null;

  return {
    r: parseInt(normalizedColor.slice(1, 3), 16),
    g: parseInt(normalizedColor.slice(3, 5), 16),
    b: parseInt(normalizedColor.slice(5, 7), 16),
  };
};

const ClassScheduler = () => {
  const { selectedAcademicYear, user } = useAppContext();
  const storedUser = user || parseStoredJson('userData');
  const batchOrderStorageKey =
    selectedAcademicYear?.id && storedUser?.id
      ? `classSchedulerBatchOrder:${storedUser.id}:${selectedAcademicYear.id}`
      : selectedAcademicYear?.id
        ? `classSchedulerBatchOrder:anonymous:${selectedAcademicYear.id}`
        : null;
  const sessionBlockDisplayStorageKey = storedUser?.id
    ? `classSchedulerSessionBlockDisplay:${storedUser.id}`
    : 'classSchedulerSessionBlockDisplay:anonymous';
  const teacherColorOverridesStorageKey = storedUser?.id
    ? `classSchedulerTeacherColors:${storedUser.id}`
    : 'classSchedulerTeacherColors:anonymous';
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0],
  );
  const [batches, setBatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewSession, setViewSession] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [draggingSession, setDraggingSession] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ left: 0, top: 0 });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [resizingSession, setResizingSession] = useState(null);
  const [resizeEdge, setResizeEdge] = useState(null); // 'left' or 'right'
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartTime, setResizeStartTime] = useState(null);
  const [resizePreview, setResizePreview] = useState(null); // {id, time_from, time_to}
  const [loadingSessionIds, setLoadingSessionIds] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null); // {x, y, session}
  const [activeSubmenu, setActiveSubmenu] = useState(null); // 'type', 'subject', 'teacher', 'room'
  const [draggingNewSession, setDraggingNewSession] = useState(null); // {batchId, startX, currentX}
  const [newSessionPreview, setNewSessionPreview] = useState(null); // {batchId, time_from, time_to}
  const [pendingSession, setPendingSession] = useState(null); // {batchId, date, time_from, time_to, room_id}
  const [showTypeMenu, setShowTypeMenu] = useState(false); // Show type selection menu first
  const [snapMinutes, setSnapMinutes] = useState(5); // Snap interval in minutes
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyTargetDate, setCopyTargetDate] = useState(
    new Date().toISOString().split('T')[0],
  );
  const [copying, setCopying] = useState(false);
  const [targetDateSessions, setTargetDateSessions] = useState(null);
  const [copyMode, setCopyMode] = useState('merge'); // 'merge' or 'replace'
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [selectedSessions, setSelectedSessions] = useState([]); // Array of selected session IDs
  const [draggingBatchId, setDraggingBatchId] = useState(null);
  const [dragOverBatchId, setDragOverBatchId] = useState(null);
  const [hoveredSessionTooltip, setHoveredSessionTooltip] = useState(null);
  const [sessionBlockDisplays, setSessionBlockDisplays] = useState(
    DEFAULT_SESSION_BLOCK_DISPLAYS,
  );
  const [teacherColorOverrides, setTeacherColorOverrides] = useState({});
  const teacherColorInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    batch_id: '',
    date: new Date().toISOString().split('T')[0],
    time_from: '',
    time_to: '',
    session_type: 'class',
    teacher_id: '',
    subject_id: '',
    room_id: '',
    notes: '',
  });

  // Timeline configuration
  const timelineStart = 6; // 6 AM
  const timelineEnd = 22; // 10 PM
  const hoursRange = timelineEnd - timelineStart;

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line
  }, [selectedAcademicYear]);

  useEffect(() => {
    if (!batchOrderStorageKey || batches.length === 0) return;

    localStorage.setItem(
      batchOrderStorageKey,
      JSON.stringify(batches.map((batch) => batch.id)),
    );
  }, [batches, batchOrderStorageKey]);

  useEffect(() => {
    const storedDisplay = localStorage.getItem(sessionBlockDisplayStorageKey);
    if (!storedDisplay) {
      setSessionBlockDisplays(DEFAULT_SESSION_BLOCK_DISPLAYS);
      return;
    }

    let parsedDisplay = null;

    try {
      parsedDisplay = JSON.parse(storedDisplay);
    } catch (error) {
      parsedDisplay = storedDisplay;
    }

    const nextDisplays = Array.isArray(parsedDisplay)
      ? parsedDisplay.filter((value) =>
          SESSION_BLOCK_DISPLAY_OPTIONS.some(
            (option) => option.value === value,
          ),
        )
      : SESSION_BLOCK_DISPLAY_OPTIONS.some(
            (option) => option.value === parsedDisplay,
          )
        ? [parsedDisplay]
        : [];

    setSessionBlockDisplays(
      nextDisplays.length > 0 ? nextDisplays : DEFAULT_SESSION_BLOCK_DISPLAYS,
    );
  }, [sessionBlockDisplayStorageKey]);

  useEffect(() => {
    localStorage.setItem(
      sessionBlockDisplayStorageKey,
      JSON.stringify(sessionBlockDisplays),
    );
  }, [sessionBlockDisplays, sessionBlockDisplayStorageKey]);

  useEffect(() => {
    const storedOverrides = parseStoredJson(teacherColorOverridesStorageKey);
    if (!storedOverrides || typeof storedOverrides !== 'object') {
      setTeacherColorOverrides({});
      return;
    }

    const normalizedOverrides = Object.entries(storedOverrides).reduce(
      (result, [teacherId, colorValue]) => {
        const normalizedColor = normalizeHexColor(String(colorValue || ''));
        if (normalizedColor) {
          result[teacherId] = normalizedColor;
        }
        return result;
      },
      {},
    );

    setTeacherColorOverrides(normalizedOverrides);
  }, [teacherColorOverridesStorageKey]);

  useEffect(() => {
    localStorage.setItem(
      teacherColorOverridesStorageKey,
      JSON.stringify(teacherColorOverrides),
    );
  }, [teacherColorOverrides, teacherColorOverridesStorageKey]);

  useEffect(() => {
    if (selectedDate && teachers.length && subjects.length && rooms.length) {
      fetchSessions();
    }
    // eslint-disable-next-line
  }, [selectedDate, teachers, subjects, rooms]);

  // Auto-dismiss error after 4 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Auto-dismiss success after 4 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  // Handle keyboard events for delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && selectedSessions.length > 0) {
        e.preventDefault();
        handleDeleteMultiple();
      }
      if (e.key === 'Escape') {
        setSelectedSessions([]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedSessions]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();

      if (!selectedAcademicYear || !selectedAcademicYear.id) {
        setError('Please select an academic year.');
        setLoading(false);
        return;
      }

      const [batchesRes, staffRes, subjectsRes, roomsRes] = await Promise.all([
        axios.get(`${appConstants.API_ROOT_URL}get-batches-v3`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        }),
        axios.post(
          `${appConstants.API_ROOT_URL}get-staff`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        axios.post(
          `${appConstants.API_ROOT_URL}get-subjects`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        axios.post(
          `${appConstants.API_ROOT_URL}get_rooms`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ]);

      const fetchedBatches = batchesRes.data.data || [];
      const savedOrder = batchOrderStorageKey
        ? parseStoredJson(batchOrderStorageKey)
        : null;

      if (Array.isArray(savedOrder) && savedOrder.length > 0) {
        const orderMap = new Map(savedOrder.map((id, index) => [id, index]));
        fetchedBatches.sort((firstBatch, secondBatch) => {
          const firstIndex = orderMap.has(firstBatch.id)
            ? orderMap.get(firstBatch.id)
            : Number.MAX_SAFE_INTEGER;
          const secondIndex = orderMap.has(secondBatch.id)
            ? orderMap.get(secondBatch.id)
            : Number.MAX_SAFE_INTEGER;

          if (firstIndex !== secondIndex) {
            return firstIndex - secondIndex;
          }

          return 0;
        });
      }

      setBatches(fetchedBatches);
      setTeachers(staffRes.data.data || []);
      setSubjects(subjectsRes.data.data || []);
      setRooms(roomsRes.data.data || []);
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get_sessions`,
        { date: selectedDate },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        // Enrich sessions with teacher, subject, and room names
        const enrichedSessions = (response.data.data || []).map((session) => {
          const teacher = teachers.find((t) => t.id === session.teacher_id);
          const subject = subjects.find((s) => s.id === session.subject_id);
          const room = rooms.find((r) => r.id === session.room_id);
          return {
            ...session,
            teacher_code: teacher?.staff_code || '',
            teacher_name: teacher?.name || '',
            subject_name: subject?.name || session.subject_name || '',
            room_name: room?.name || '',
          };
        });
        setSessions(enrichedSessions);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    <div>{hoveredSessionTooltip.timeLabel}</div>;
    try {
      const token = getAuthToken();
      const payload = {
        date: formData.date,
        time_from: formData.time_from,
        time_to: formData.time_to,
        batch_id: parseInt(formData.batch_id),
        session_type: formData.session_type,
        notes: formData.notes,
      };

      // Add optional fields based on session type
      if (formData.session_type === 'class') {
        if (formData.teacher_id)
          payload.teacher_id = parseInt(formData.teacher_id);
        if (formData.subject_id)
          payload.subject_id = parseInt(formData.subject_id);
      }
      if (formData.room_id) payload.room_id = parseInt(formData.room_id);

      const endpoint = editingSession ? 'update_session' : 'create_session';
      if (editingSession) {
        payload.id = editingSession.id;
        setLoadingSessionIds((prev) => new Set(prev).add(editingSession.id));
      }

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${endpoint}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setSuccess(
          response.data.message ||
            `Session ${editingSession ? 'updated' : 'created'} successfully`,
        );
        handleCloseModal();
        fetchSessions();
      }
    } catch (err) {
      console.error('Error saving session:', err);
      setError(err.response?.data?.message || 'Failed to save session');
    } finally {
      setSubmitting(false);
      if (editingSession) {
        setLoadingSessionIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(editingSession.id);
          return newSet;
        });
      }
    }
  };

  const handleEdit = async (session) => {
    setEditingSession(session);
    setFormData({
      batch_id: session.batch_id.toString(),
      date: session.date,
      time_from: session.time_from,
      time_to: session.time_to,
      session_type: session.session_type,
      teacher_id: session.teacher_id?.toString() || '',
      subject_id: session.subject_id?.toString() || '',
      room_id: session.room_id?.toString() || '',
      notes: session.notes || '',
    });
    setShowViewModal(false);
    setShowModal(true);
  };

  const handleView = (session) => {
    setViewSession(session);
    setShowViewModal(true);
  };

  const handleCreateWithSubjectTeacher = async (
    subjectId,
    teacherId,
    sessionType = 'class',
  ) => {
    if (!pendingSession) return;

    setContextMenu(null);
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = getAuthToken();
      const payload = {
        batch_id: pendingSession.batchId,
        date: pendingSession.date,
        time_from: pendingSession.time_from,
        time_to: pendingSession.time_to,
        session_type: sessionType,
        subject_id: subjectId,
        teacher_id: teacherId,
        room_id: pendingSession.room_id,
        status: '',
        notes: '',
      };

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}create_session`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setSuccess('Session created successfully');
        fetchSessions();
        setPendingSession(null);
      }
    } catch (err) {
      console.error('Error creating session:', err);
      setError(err.response?.data?.message || 'Failed to create session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateWithType = async (type) => {
    if (!pendingSession) return;

    setContextMenu(null);
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = getAuthToken();
      const payload = {
        batch_id: pendingSession.batchId,
        date: pendingSession.date,
        time_from: pendingSession.time_from,
        time_to: pendingSession.time_to,
        session_type: type,
        subject_id: null,
        teacher_id: null,
        room_id: pendingSession.room_id,
        status: '',
        notes: type === 'exam' ? 'Exam' : type === 'break' ? 'Break' : '',
      };

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}create_session`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setSuccess('Session created successfully');
        fetchSessions();
        setPendingSession(null);
      }
    } catch (err) {
      console.error('Error creating session:', err);
      setError(err.response?.data?.message || 'Failed to create session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (session) => {
    if (!window.confirm('Are you sure you want to delete this session?')) {
      return;
    }

    setError('');
    setSuccess('');
    setContextMenu(null);

    try {
      const token = getAuthToken();
      setLoadingSessionIds((prev) => new Set(prev).add(session.id));
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}delete_session?id=${session.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setSuccess(response.data.message || 'Session deleted successfully');
        fetchSessions();
      }
    } catch (err) {
      console.error('Error deleting session:', err);
      setError(err.response?.data?.message || 'Failed to delete session');
    } finally {
      setLoadingSessionIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(session.id);
        return newSet;
      });
    }
  };

  const handleDeleteMultiple = async () => {
    if (selectedSessions.length === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedSessions.length} session(s)?`;
    if (!window.confirm(confirmMessage)) return;

    setError('');
    setSuccess('');

    try {
      const token = getAuthToken();
      const deletePromises = selectedSessions.map((sessionId) => {
        const session = sessions.find((s) => s.id === sessionId);
        if (!session) return null;
        return axios.get(
          `${appConstants.API_ROOT_URL}delete_session?id=${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      });

      await Promise.all(deletePromises.filter((p) => p !== null));
      setSuccess(`Successfully deleted ${selectedSessions.length} session(s)`);
      setSelectedSessions([]);
      fetchSessions();
    } catch (err) {
      console.error('Error deleting sessions:', err);
      setError(err.response?.data?.message || 'Failed to delete sessions');
    }
  };

  const handleContextMenu = (e, session) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      session: session,
    });
  };

  const handleQuickUpdate = async (session, field, value) => {
    setContextMenu(null);
    setError('');
    setSuccess('');

    try {
      const token = getAuthToken();
      setLoadingSessionIds((prev) => new Set(prev).add(session.id));

      const payload = {
        id: session.id,
        batch_id: session.batch_id,
        date: session.date,
        time_from: session.time_from,
        time_to: session.time_to,
        session_type: field === 'session_type' ? value : session.session_type,
      };

      if (field === 'teacher_id') {
        payload.teacher_id = value || null;
      } else if (session.teacher_id) {
        payload.teacher_id = session.teacher_id;
      }

      if (field === 'subject_id') {
        payload.subject_id = value || null;
      } else if (session.subject_id) {
        payload.subject_id = session.subject_id;
      }

      if (field === 'room_id') {
        payload.room_id = value || null;
      } else if (session.room_id) {
        payload.room_id = session.room_id;
      }

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}update_session`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setSuccess('Session updated successfully');
        fetchSessions();
      }
    } catch (err) {
      console.error('Error updating session:', err);
      setError(err.response?.data?.message || 'Failed to update session');
    } finally {
      setLoadingSessionIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(session.id);
        return newSet;
      });
    }
  };

  const handleBatchRoomUpdate = async (batch, roomId) => {
    setError('');
    setSuccess('');

    try {
      const token = getAuthToken();
      const payload = {
        id: batch.id,
        room_id: roomId || null,
      };

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}update-batch`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setSuccess('Batch room updated successfully');
        fetchInitialData();
      }
    } catch (err) {
      console.error('Error updating batch room:', err);
      setError(err.response?.data?.message || 'Failed to update batch room');
    }
  };

  const checkTargetDateSessions = async (date) => {
    if (!date || date === selectedDate) {
      setTargetDateSessions(null);
      return;
    }

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get_sessions`,
        { date },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setTargetDateSessions(response.data.data || []);
      }
    } catch (err) {
      console.error('Error checking target date:', err);
      setTargetDateSessions(null);
    }
  };

  const handleCopySchedule = async () => {
    if (!copyTargetDate || copyTargetDate === selectedDate) {
      setError('Please select a different date');
      return;
    }

    setCopying(true);
    setError('');
    setSuccess('');

    try {
      const token = getAuthToken();

      // Delete existing sessions if replace mode
      if (
        copyMode === 'replace' &&
        targetDateSessions &&
        targetDateSessions.length > 0
      ) {
        const deletePromises = targetDateSessions.map((session) =>
          axios.get(
            `${appConstants.API_ROOT_URL}delete_session?id=${session.id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        );
        await Promise.all(deletePromises);
      }

      // Copy all sessions from current date to target date
      const copyPromises = sessions.map((session) => {
        const payload = {
          batch_id: session.batch_id,
          date: copyTargetDate,
          time_from: session.time_from,
          time_to: session.time_to,
          session_type: session.session_type,
          status: session.status || '',
          notes: session.notes || '',
        };

        if (session.teacher_id) payload.teacher_id = session.teacher_id;
        if (session.subject_id) payload.subject_id = session.subject_id;
        if (session.room_id) payload.room_id = session.room_id;

        return axios.post(
          `${appConstants.API_ROOT_URL}create_session`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      });

      await Promise.all(copyPromises);

      setSuccess(
        `Successfully copied ${sessions.length} sessions to ${copyTargetDate}`,
      );
      setShowCopyModal(false);

      // Switch to the target date to show copied sessions
      setSelectedDate(copyTargetDate);
    } catch (err) {
      console.error('Error copying schedule:', err);
      setError(err.response?.data?.message || 'Failed to copy schedule');
    } finally {
      setCopying(false);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleResetBatchOrder = () => {
    if (batchOrderStorageKey) {
      localStorage.removeItem(batchOrderStorageKey);
    }
    setDraggingBatchId(null);
    setDragOverBatchId(null);
    fetchInitialData();
  };

  const handleBatchDragStart = (e, batchId) => {
    setDraggingBatchId(batchId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(batchId));
  };

  const handleBatchDragOver = (e, batchId) => {
    if (!draggingBatchId || draggingBatchId === batchId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverBatchId !== batchId) {
      setDragOverBatchId(batchId);
    }
  };

  const handleBatchDrop = (e, targetBatchId) => {
    if (!draggingBatchId || draggingBatchId === targetBatchId) {
      setDraggingBatchId(null);
      setDragOverBatchId(null);
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    setBatches((prevBatches) => {
      const fromIndex = prevBatches.findIndex(
        (batch) => batch.id === draggingBatchId,
      );
      const toIndex = prevBatches.findIndex(
        (batch) => batch.id === targetBatchId,
      );

      if (fromIndex === -1 || toIndex === -1) {
        return prevBatches;
      }

      return moveItem(prevBatches, fromIndex, toIndex);
    });

    setDraggingBatchId(null);
    setDragOverBatchId(null);
  };

  const handleBatchDragEnd = () => {
    setDraggingBatchId(null);
    setDragOverBatchId(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowViewModal(false);
    setViewSession(null);
    setEditingSession(null);
    setFormData({
      batch_id: '',
      date: selectedDate,
      time_from: '',
      time_to: '',
      session_type: 'class',
      teacher_id: '',
      subject_id: '',
      room_id: '',
      notes: '',
    });
  };

  const handleAddSession = (batchId) => {
    setFormData({
      batch_id: batchId.toString(),
      date: selectedDate,
      time_from: '',
      time_to: '',
      session_type: 'class',
      teacher_id: '',
      subject_id: '',
      room_id: '',
      notes: '',
    });
    setShowModal(true);
  };

  const getSessionPosition = (timeFrom, timeTo) => {
    const [fromHour, fromMin] = timeFrom.split(':').map(Number);
    const [toHour, toMin] = timeTo.split(':').map(Number);

    const startMinutes = (fromHour - timelineStart) * 60 + fromMin;
    const endMinutes = (toHour - timelineStart) * 60 + toMin;
    const totalMinutes = hoursRange * 60;

    const left = (startMinutes / totalMinutes) * 100;
    const width = ((endMinutes - startMinutes) / totalMinutes) * 100;

    return { left: `${left}%`, width: `${width}%` };
  };

  const getTeacherAutoColor = (teacherSeed) => {
    if (!teacherSeed) {
      return DEFAULT_CLASS_BLOCK_COLOR;
    }

    const hue = getHashHue(teacherSeed);
    return `hsl(${hue}, 58%, 44%)`;
  };

  const getTeacherBlockColor = (teacher) => {
    const teacherId = teacher?.id != null ? String(teacher.id) : '';
    if (teacherId && teacherColorOverrides[teacherId]) {
      return teacherColorOverrides[teacherId];
    }

    return getTeacherAutoColor(
      teacher?.staff_code || teacher?.name || teacher?.id || '',
    );
  };

  const getSessionBlockStyle = (session) => {
    if (session.session_type === 'break') {
      return {
        background: DEFAULT_BREAK_BLOCK_COLOR,
        color: '#ffffff',
        border: '1px solid rgba(255,255,255,0.16)',
      };
    }

    if (session.session_type === 'exam') {
      return {
        background: DEFAULT_EXAM_BLOCK_COLOR,
        color: '#ffffff',
        border: '1px solid rgba(255,255,255,0.16)',
      };
    }

    const teacherSeed =
      session.teacher_code || session.teacher_name || session.teacher_id;

    if (!teacherSeed) {
      return {
        background: '#d1d5db',
        color: '#111827',
        border: '1px solid rgba(15,23,42,0.08)',
      };
    }

    return {
      background: '#e5e7eb',
      color: '#111827',
      border: '1px solid rgba(15,23,42,0.08)',
    };
  };

  const getSessionDisplayTextStyle = (session, displayKey) => {
    if (session.session_type === 'break' || session.session_type === 'exam') {
      return {
        color: '#ffffff',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.22)',
      };
    }

    if (displayKey === 'teacher_code' && session.teacher_id) {
      const teacher = teachers.find(
        (item) => String(item.id) === String(session.teacher_id),
      );

      return {
        color: getTeacherBlockColor(teacher),
        textShadow: 'none',
        fontWeight: 800,
      };
    }

    return {
      color: '#111827',
      textShadow: 'none',
    };
  };

  const handleTeacherColorChange = (teacherId, colorValue) => {
    const normalizedColor = normalizeHexColor(colorValue);
    if (!normalizedColor) {
      return;
    }

    setTeacherColorOverrides((currentOverrides) => ({
      ...currentOverrides,
      [String(teacherId)]: normalizedColor,
    }));
  };

  const handleTeacherColorReset = (teacherId) => {
    setTeacherColorOverrides((currentOverrides) => {
      const nextOverrides = { ...currentOverrides };
      delete nextOverrides[String(teacherId)];
      return nextOverrides;
    });
  };

  const openTeacherColorPicker = (teacherId) => {
    const teacher = teachers.find(
      (item) => String(item.id) === String(teacherId),
    );
    if (!teacher) {
      setError('Teacher not found for this session');
      return;
    }

    const colorInput = teacherColorInputRef.current;
    if (!colorInput) {
      return;
    }

    colorInput.dataset.teacherId = String(teacher.id);
    colorInput.value =
      normalizeHexColor(getTeacherBlockColor(teacher)) ||
      DEFAULT_CLASS_BLOCK_COLOR;
    colorInput.click();
  };

  const handleTeacherColorInputChange = (event) => {
    const teacherId = event.target.dataset.teacherId;
    if (!teacherId) {
      return;
    }

    handleTeacherColorChange(teacherId, event.target.value);
  };

  const formatTime12Hour = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'pm' : 'am';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, '0')}${period}`;
  };

  const getSessionIcon = (type) => {
    switch (type) {
      case 'class':
        return <FaChalkboardTeacher />;
      case 'exam':
        return <FaBook />;
      case 'break':
        return <FaCoffee />;
      default:
        return <FaClock />;
    }
  };

  const getSessionTypeLabel = (type) => {
    switch (type) {
      case 'class':
        return 'Class';
      case 'exam':
        return 'Exam';
      case 'break':
        return 'Break';
      default:
        return 'Session';
    }
  };

  const getSessionDisplayText = (session, displayKey) => {
    switch (displayKey) {
      case 'subject':
        return (
          session.subject_name ||
          (session.session_type === 'break'
            ? 'Break'
            : getSessionTypeLabel(session.session_type))
        );
      case 'time':
        return `${formatTime12Hour(session.time_from.substring(0, 5))} - ${formatTime12Hour(session.time_to.substring(0, 5))}`;
      case 'type':
        return getSessionTypeLabel(session.session_type);
      case 'teacher_code':
      default:
        return session.teacher_code || session.teacher_name || 'TBD';
    }
  };

  const getSelectedSessionDisplayItems = (session) => {
    const activeDisplays =
      sessionBlockDisplays.length > 0
        ? sessionBlockDisplays
        : DEFAULT_SESSION_BLOCK_DISPLAYS;

    return activeDisplays.map((displayKey) => ({
      key: displayKey,
      text: getSessionDisplayText(session, displayKey),
    }));
  };

  const toggleSessionBlockDisplay = (displayValue) => {
    setSessionBlockDisplays((currentDisplays) => {
      const isSelected = currentDisplays.includes(displayValue);

      if (isSelected) {
        if (currentDisplays.length === 1) {
          return currentDisplays;
        }

        return currentDisplays.filter((value) => value !== displayValue);
      }

      return [...currentDisplays, displayValue];
    });
  };

  const getSessionDisplaySummary = () => {
    if (sessionBlockDisplays.length === 0) {
      return 'Teacher Code';
    }

    if (sessionBlockDisplays.length === 1) {
      return (
        SESSION_BLOCK_DISPLAY_OPTIONS.find(
          (option) => option.value === sessionBlockDisplays[0],
        )?.label || 'Teacher Code'
      );
    }

    return `${sessionBlockDisplays.length} items`;
  };

  const getBatchSessions = (batchId) => {
    return sessions.filter((s) => s.batch_id === batchId);
  };

  const getSessionTooltipData = (session) => {
    const sessionLabel =
      session.session_type === 'break'
        ? 'Break'
        : session.subject_name || session.session_type;
    const title = session.teacher_name
      ? `${sessionLabel} - ${session.teacher_name}`
      : sessionLabel;

    return {
      title,
      timeLabel: `${formatTime12Hour(session.time_from.substring(0, 5))}  -  ${formatTime12Hour(session.time_to.substring(0, 5))}`,
      roomName: session.room_name || '',
      notes: session.notes || '',
    };
  };

  const handleSessionMouseEnter = (event, session) => {
    const tooltipData = getSessionTooltipData(session);
    const { bottom, left, width } = event.currentTarget.getBoundingClientRect();
    const showTimer = window.setTimeout(() => {
      setHoveredSessionTooltip({
        sessionId: session.id,
        top: bottom + 10,
        left: left + width / 2,
        ...tooltipData,
      });
    }, 200);

    event.currentTarget.dataset.tooltipTimer = String(showTimer);
  };

  const handleSessionMouseLeave = (event) => {
    const sessionId = Number(event.currentTarget.dataset.sessionId);
    const showTimer = event.currentTarget.dataset.tooltipTimer;
    if (showTimer) {
      window.clearTimeout(Number(showTimer));
      delete event.currentTarget.dataset.tooltipTimer;
    }

    setHoveredSessionTooltip((prev) => {
      if (!prev) return null;
      return prev.sessionId === sessionId ? null : prev;
    });
  };

  const hideSessionTooltip = () => {
    setHoveredSessionTooltip(null);
  };

  // Check if selected date is today
  const isToday = () => {
    const today = new Date().toISOString().split('T')[0];
    return selectedDate === today;
  };

  // Calculate current time position on timeline
  const getCurrentTimePosition = () => {
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const seconds = currentTime.getSeconds();
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    const timelineStartSeconds = timelineStart * 3600;
    const timelineEndSeconds = timelineEnd * 3600;

    // Only show if current time is within timeline range (6 AM - 10 PM)
    if (
      totalSeconds < timelineStartSeconds ||
      totalSeconds > timelineEndSeconds
    ) {
      return null;
    }

    // Calculate position in pixels (timeline is 1600px for 16 hours)
    const secondsFromStart = totalSeconds - timelineStartSeconds;
    const totalTimelineSeconds = (timelineEnd - timelineStart) * 3600;
    const positionPx = (secondsFromStart / totalTimelineSeconds) * 1600;

    return positionPx;
  };

  // Drag and drop handlers
  const handleDragStart = (e, session) => {
    setDraggingSession(session);
    e.dataTransfer.effectAllowed = 'move';

    // Store the offset from mouse to the left edge of the block
    const blockRect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - blockRect.left;
    e.dataTransfer.setData('offsetX', offsetX.toString());
    e.dataTransfer.setData('altKey', e.altKey.toString());
  };

  const handleDragOver = (e) => {
    if (!draggingSession) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetBatchId) => {
    e.preventDefault();
    if (!draggingSession) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const storedOffsetX = parseFloat(e.dataTransfer.getData('offsetX')) || 0;
    const wasCopying = e.dataTransfer.getData('altKey') === 'true';

    // Calculate position based on block's left edge, not mouse position
    const blockLeftX = e.clientX - storedOffsetX - rect.left;
    const totalWidth = rect.width;
    const totalMinutes = hoursRange * 60;
    const minutesFromStart = (blockLeftX / totalWidth) * totalMinutes;

    // Snap to configured interval
    const snappedMinutes =
      Math.round(minutesFromStart / snapMinutes) * snapMinutes;
    const clickedHour = Math.floor(snappedMinutes / 60) + timelineStart;
    const clickedMin = snappedMinutes % 60;

    // Calculate session duration
    const [fromH, fromM] = draggingSession.time_from.split(':').map(Number);
    const [toH, toM] = draggingSession.time_to.split(':').map(Number);
    const durationMinutes = toH * 60 + toM - (fromH * 60 + fromM);

    // New times
    const newFromTime = `${String(clickedHour).padStart(2, '0')}:${String(
      clickedMin,
    ).padStart(2, '0')}:00`;
    const newToMinutes = clickedHour * 60 + clickedMin + durationMinutes;
    const newToHour = Math.floor(newToMinutes / 60);
    const newToMin = newToMinutes % 60;
    const newToTime = `${String(newToHour).padStart(2, '0')}:${String(
      newToMin,
    ).padStart(2, '0')}:00`;

    try {
      const token = getAuthToken();

      // If copying (Ctrl was held), create new session; otherwise update existing
      const endpoint = wasCopying ? 'create_session' : 'update_session';
      const payload = {
        batch_id: targetBatchId,
        date: draggingSession.date,
        time_from: newFromTime,
        time_to: newToTime,
        session_type: draggingSession.session_type,
        status: draggingSession.status || '',
        notes: draggingSession.notes || '',
      };

      // Only add id for update (move), not for create (copy)
      if (!wasCopying) {
        payload.id = draggingSession.id;
        setLoadingSessionIds((prev) => new Set(prev).add(draggingSession.id));
      }

      if (draggingSession.teacher_id)
        payload.teacher_id = draggingSession.teacher_id;
      if (draggingSession.subject_id)
        payload.subject_id = draggingSession.subject_id;

      // Use destination batch's room for both copy and move
      const targetBatch = batches.find((b) => b.id === targetBatchId);
      if (targetBatch?.room_id) payload.room_id = targetBatch.room_id;

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${endpoint}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setSuccess(
          wasCopying
            ? 'Session copied successfully'
            : 'Session moved successfully',
        );
        fetchSessions();
      }
    } catch (err) {
      console.error('Error with session:', err);
      setError(
        err.response?.data?.message ||
          `Failed to ${wasCopying ? 'copy' : 'move'} session`,
      );
    } finally {
      setLoadingSessionIds((prev) => {
        const newSet = new Set(prev);
        if (draggingSession) newSet.delete(draggingSession.id);
        return newSet;
      });
      setDraggingSession(null);
    }
  };

  // Ctrl+drag panning handlers
  const handleMouseDown = (e) => {
    if (e.ctrlKey && e.button === 0) {
      e.preventDefault();
      e.stopPropagation();
      const container = e.currentTarget;
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      setScrollStart({
        left: container.scrollLeft,
        top: container.scrollTop,
      });
      container.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e) => {
    if (!isPanning) return;
    e.preventDefault();
    const container = document.querySelector('.scheduler-container');
    if (!container) return;

    const dx = e.clientX - panStart.x;
    const dy = e.clientY - panStart.y;

    container.scrollLeft = scrollStart.left - dx;
    container.scrollTop = scrollStart.top - dy;
  };

  const handleMouseUp = () => {
    if (isPanning) {
      const container = document.querySelector('.scheduler-container');
      if (container) {
        container.style.cursor = '';
      }
      setIsPanning(false);
    }
  };

  useEffect(() => {
    const handleMove = (e) => {
      if (!isPanning) return;
      e.preventDefault();
      const container = document.querySelector('.scheduler-container');
      if (!container) return;

      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;

      container.scrollLeft = scrollStart.left - dx;
      container.scrollTop = scrollStart.top - dy;
    };

    const handleUp = () => {
      if (isPanning) {
        const container = document.querySelector('.scheduler-container');
        if (container) {
          container.style.cursor = '';
        }
        setIsPanning(false);
      }
    };

    if (isPanning) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
      };
    }
  }, [isPanning, panStart, scrollStart]);

  // Handle drag-to-create mouse move and up
  useEffect(() => {
    if (draggingNewSession) {
      const handleMove = (e) => {
        const { batchId, startX, rect } = draggingNewSession;
        const currentX = e.clientX - rect.left;

        // Calculate time range
        const totalWidth = rect.width;
        const totalMinutes = hoursRange * 60;

        const startMinutes =
          (Math.min(startX, currentX) / totalWidth) * totalMinutes;
        const endMinutes =
          (Math.max(startX, currentX) / totalWidth) * totalMinutes;

        // Snap to configured interval
        const snappedStartMinutes =
          Math.round(startMinutes / snapMinutes) * snapMinutes;
        const snappedEndMinutes =
          Math.round(endMinutes / snapMinutes) * snapMinutes;

        if (snappedEndMinutes > snappedStartMinutes) {
          const startHour =
            Math.floor(snappedStartMinutes / 60) + timelineStart;
          const startMin = snappedStartMinutes % 60;
          const endHour = Math.floor(snappedEndMinutes / 60) + timelineStart;
          const endMin = snappedEndMinutes % 60;

          const time_from = `${String(startHour).padStart(2, '0')}:${String(
            startMin,
          ).padStart(2, '0')}:00`;
          const time_to = `${String(endHour).padStart(2, '0')}:${String(
            endMin,
          ).padStart(2, '0')}:00`;

          setNewSessionPreview({ batchId, time_from, time_to });
          setDraggingNewSession({
            ...draggingNewSession,
            currentX,
            time_from,
            time_to,
          });
        } else {
          setDraggingNewSession({ ...draggingNewSession, currentX });
        }
      };

      const handleUp = (e) => {
        // Use time data stored in draggingNewSession
        const { time_from, time_to, batchId } = draggingNewSession;

        // Clear dragging and preview state
        setDraggingNewSession(null);
        setNewSessionPreview(null);

        // Then process if we have valid time range
        if (time_from && time_to) {
          const batch = batches.find((b) => b.id === batchId);
          setPendingSession({
            batchId: batchId,
            date: selectedDate,
            time_from: time_from,
            time_to: time_to,
            room_id: batch?.room_id || null,
          });

          // Open type selection menu first
          setTimeout(() => {
            setContextMenu({
              x: e.clientX,
              y: e.clientY,
              mode: 'select-type',
            });
          }, 10);
        }
      };

      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);

      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
      };
    }
  }, [draggingNewSession, batches, selectedDate, hoursRange, timelineStart]);

  // Click on blank area to create session
  const handleTrackMouseDown = (e, batchId) => {
    // Check if clicked on session block or resize handle
    if (
      e.target.closest('.session-block') ||
      e.target.closest('.resize-handle')
    )
      return;
    if (e.button !== 0) return; // Only left mouse button

    const rect = e.currentTarget.getBoundingClientRect();
    const startX = e.clientX - rect.left;

    setDraggingNewSession({ batchId, startX, currentX: startX, rect });
    e.preventDefault();
  };

  const handleTrackClick = (e, batchId) => {
    // Check if clicked on session block
    if (e.target.closest('.session-block')) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const totalWidth = rect.width;
    const totalMinutes = hoursRange * 60;
    const clickedMinutes = (offsetX / totalWidth) * totalMinutes;
    const clickedHour = Math.floor(clickedMinutes / 60) + timelineStart;
    const clickedMin = Math.floor(clickedMinutes % 60);

    // Round to configured snap interval
    const roundedMin = Math.round(clickedMin / snapMinutes) * snapMinutes;
    const finalHour = roundedMin === 60 ? clickedHour + 1 : clickedHour;
    const finalMin = roundedMin === 60 ? 0 : roundedMin;

    const fromTime = `${String(finalHour).padStart(2, '0')}:${String(
      finalMin,
    ).padStart(2, '0')}`;
    const toHour = finalMin + 45 >= 60 ? finalHour + 1 : finalHour;
    const toMin = (finalMin + 45) % 60;
    const toTime = `${String(toHour).padStart(2, '0')}:${String(toMin).padStart(
      2,
      '0',
    )}`;

    const targetBatch = batches.find((b) => b.id === batchId);
    setFormData({
      batch_id: batchId.toString(),
      date: selectedDate,
      time_from: fromTime,
      time_to: toTime,
      session_type: 'class',
      teacher_id: '',
      subject_id: '',
      room_id: targetBatch?.room_id?.toString() || '',
      notes: '',
    });
    setEditingSession(null);
    setShowModal(true);
  };

  // Resize handlers for session blocks
  const handleResizeStart = (e, session, edge) => {
    e.stopPropagation();
    e.preventDefault();

    const startX = e.clientX;
    const startTime = {
      time_from: session.time_from,
      time_to: session.time_to,
    };

    let hasMoved = false;

    const handleMove = (moveEvent) => {
      // Only activate resize if mouse has moved more than 3 pixels
      if (!hasMoved && Math.abs(moveEvent.clientX - startX) < 3) {
        return;
      }

      if (!hasMoved) {
        hasMoved = true;
        setResizingSession(session);
        setResizeEdge(edge);
        setResizeStartX(startX);
        setResizeStartTime(startTime);
      }
    };

    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);

      // If no movement occurred, don't activate resize
      if (!hasMoved) {
        return;
      }
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  useEffect(() => {
    const handleResizeMove = (e) => {
      if (!resizingSession || !resizeEdge) return;

      const deltaX = e.clientX - resizeStartX;
      const totalWidth = 1600; // Timeline width in pixels
      const totalMinutes = hoursRange * 60;
      const minutesPerPixel = totalMinutes / totalWidth;
      const deltaMinutes =
        Math.round((deltaX * minutesPerPixel) / snapMinutes) * snapMinutes; // Snap to configured interval

      const [origFromH, origFromM] = resizeStartTime.time_from
        .split(':')
        .map(Number);
      const [origToH, origToM] = resizeStartTime.time_to.split(':').map(Number);

      let newFromMinutes = origFromH * 60 + origFromM;
      let newToMinutes = origToH * 60 + origToM;

      if (resizeEdge === 'left') {
        newFromMinutes += deltaMinutes;
        // Prevent overlap (at least 15 min duration)
        if (newFromMinutes >= newToMinutes - 15) {
          newFromMinutes = newToMinutes - 15;
        }
      } else {
        newToMinutes += deltaMinutes;
        // Prevent overlap (at least 15 min duration)
        if (newToMinutes <= newFromMinutes + 15) {
          newToMinutes = newFromMinutes + 15;
        }
      }

      // Update preview for visual feedback
      const newFromH = Math.floor(newFromMinutes / 60);
      const newFromM = newFromMinutes % 60;
      const newToH = Math.floor(newToMinutes / 60);
      const newToM = newToMinutes % 60;

      setResizePreview({
        id: resizingSession.id,
        time_from: `${String(newFromH).padStart(2, '0')}:${String(
          newFromM,
        ).padStart(2, '0')}:00`,
        time_to: `${String(newToH).padStart(2, '0')}:${String(newToM).padStart(
          2,
          '0',
        )}:00`,
      });
    };

    const handleResizeEnd = async () => {
      if (!resizingSession || !resizePreview) return;

      const sessionId = resizingSession.id;
      const newTimeFrom = resizePreview.time_from;
      const newTimeTo = resizePreview.time_to;

      // Store original times for rollback
      const sessionToUpdate = sessions.find((s) => s.id === sessionId);
      if (!sessionToUpdate) return;

      const originalTimeFrom = sessionToUpdate.time_from;
      const originalTimeTo = sessionToUpdate.time_to;

      try {
        const token = getAuthToken();

        // Optimistically update the session in the UI
        setSessions((prevSessions) =>
          prevSessions.map((s) =>
            s.id === sessionId
              ? { ...s, time_from: newTimeFrom, time_to: newTimeTo }
              : s,
          ),
        );

        // Clear resize states
        setResizingSession(null);
        setResizeEdge(null);
        setResizeStartX(0);
        setResizeStartTime(null);
        setResizePreview(null);

        // Add to loading state
        setLoadingSessionIds((prev) => new Set(prev).add(sessionId));

        const payload = {
          id: sessionId,
          batch_id: sessionToUpdate.batch_id,
          date: sessionToUpdate.date,
          time_from: newTimeFrom,
          time_to: newTimeTo,
          session_type: sessionToUpdate.session_type,
        };

        if (sessionToUpdate.teacher_id)
          payload.teacher_id = sessionToUpdate.teacher_id;
        if (sessionToUpdate.subject_id)
          payload.subject_id = sessionToUpdate.subject_id;
        if (sessionToUpdate.room_id) payload.room_id = sessionToUpdate.room_id;

        await axios.post(
          `${appConstants.API_ROOT_URL}update_session`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setSuccess('Session time updated successfully');
        // Refresh to get any server-side updates
        fetchSessions();
      } catch (err) {
        console.error('Error resizing session:', err);
        setError(
          err.response?.data?.message || 'Failed to update session time',
        );
        // Revert to original position on error (without fetching)
        setSessions((prevSessions) =>
          prevSessions.map((s) =>
            s.id === sessionId
              ? { ...s, time_from: originalTimeFrom, time_to: originalTimeTo }
              : s,
          ),
        );
      } finally {
        setLoadingSessionIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sessionId);
          return newSet;
        });
      }
    };

    if (resizingSession) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [
    resizingSession,
    resizeEdge,
    resizeStartX,
    resizeStartTime,
    resizePreview,
    sessions,
    hoursRange,
  ]);

  return (
    <Layout>
      <div
        className="container-fluid"
        style={
          isFullscreen
            ? {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 9999,
                backgroundColor: '#f8f9fa',
                overflow: 'auto',
                padding: '1rem',
              }
            : {}
        }
      >
        {/* Toolbar */}
        <div
          className="row"
          style={{ marginBottom: isFullscreen ? '0.5rem' : '0.75rem' }}
        >
          <div className="col-12">
            <div
              style={{
                background: '#2c3e50',
                borderRadius: '6px',
                padding: '0.5rem 0.75rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
              }}
            >
              {/* Left: Title */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <FaClock style={{ color: '#3498db', fontSize: '1.1rem' }} />
                <span
                  style={{
                    color: '#ecf0f1',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                  }}
                >
                  Class Scheduler
                </span>
              </div>

              {/* Right: Tools */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {/* Fullscreen Button (only show when not in fullscreen) */}
                {!isFullscreen && (
                  <button
                    onClick={toggleFullscreen}
                    title="Enter Fullscreen"
                    style={{
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#ecf0f1',
                      padding: '0.4rem 0.6rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      fontSize: '0.9rem',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        'rgba(255,255,255,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        'rgba(255,255,255,0.1)';
                    }}
                  >
                    <FaExpand />
                  </button>
                )}

                {/* Copy Schedule Button */}
                <button
                  onClick={() => {
                    setShowCopyModal(true);
                    setTargetDateSessions(null);
                    setCopyMode('merge');
                  }}
                  disabled={sessions.length === 0}
                  title="Copy Schedule"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: sessions.length === 0 ? '#7f8c8d' : '#ecf0f1',
                    padding: '0.4rem 0.6rem',
                    borderRadius: '4px',
                    cursor: sessions.length === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    fontSize: '0.9rem',
                    opacity: sessions.length === 0 ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (sessions.length > 0) {
                      e.currentTarget.style.background =
                        'rgba(255,255,255,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  }}
                >
                  <FaCopy />
                </button>

                <button
                  onClick={handleResetBatchOrder}
                  disabled={batches.length === 0}
                  title="Reset batch order"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: batches.length === 0 ? '#7f8c8d' : '#ecf0f1',
                    padding: '0.4rem 0.75rem',
                    borderRadius: '4px',
                    cursor: batches.length === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    opacity: batches.length === 0 ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (batches.length > 0) {
                      e.currentTarget.style.background =
                        'rgba(255,255,255,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  }}
                >
                  Reset Order
                </button>

                {/* Separator */}
                <div
                  style={{
                    width: '1px',
                    height: '24px',
                    background: 'rgba(255,255,255,0.2)',
                  }}
                />

                {/* Snap Control */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.08)',
                    borderRadius: '4px',
                    padding: '0.25rem 0.5rem',
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                >
                  <FaClock
                    style={{
                      color: '#95a5a6',
                      fontSize: '0.75rem',
                      marginRight: '0.4rem',
                    }}
                  />
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={snapMinutes}
                    onChange={(e) =>
                      setSnapMinutes(parseInt(e.target.value) || 5)
                    }
                    style={{
                      width: '45px',
                      padding: '0.2rem 0.3rem',
                      background: 'transparent',
                      border: 'none',
                      color: '#ecf0f1',
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      outline: 'none',
                    }}
                  />
                  <span style={{ color: '#95a5a6', fontSize: '0.75rem' }}>
                    min
                  </span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.08)',
                    borderRadius: '4px',
                    padding: '0.2rem 0.35rem',
                    border: '1px solid rgba(255,255,255,0.15)',
                    gap: '0.4rem',
                  }}
                >
                  <span
                    style={{
                      color: '#95a5a6',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Show
                  </span>
                  <Dropdown autoClose="outside">
                    <Dropdown.Toggle
                      size="sm"
                      variant="link"
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.16)',
                        color: '#ecf0f1',
                        fontSize: '0.8rem',
                        minWidth: '132px',
                        padding: '0.28rem 0.6rem',
                        textDecoration: 'none',
                        boxShadow: 'none',
                      }}
                    >
                      {getSessionDisplaySummary()}
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      style={{
                        minWidth: '190px',
                        padding: '0.35rem 0.5rem',
                      }}
                    >
                      {SESSION_BLOCK_DISPLAY_OPTIONS.map((option) => (
                        <Form.Check
                          key={option.value}
                          id={`session-display-${option.value}`}
                          type="checkbox"
                          label={option.label}
                          checked={sessionBlockDisplays.includes(option.value)}
                          onChange={() =>
                            toggleSessionBlockDisplay(option.value)
                          }
                          className="mb-1"
                        />
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                <div
                  style={{
                    width: '1px',
                    height: '24px',
                    background: 'rgba(255,255,255,0.2)',
                  }}
                />

                {/* Date Picker */}
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  style={{
                    padding: '0.35rem 0.5rem',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '4px',
                    color: '#ecf0f1',
                    fontSize: '0.85rem',
                    width: '150px',
                  }}
                />

                {/* Close button (only show when in fullscreen) */}
                {isFullscreen && (
                  <>
                    {/* Separator */}
                    <div
                      style={{
                        width: '1px',
                        height: '24px',
                        background: 'rgba(255,255,255,0.2)',
                      }}
                    />

                    <button
                      onClick={toggleFullscreen}
                      title="Close Fullscreen"
                      style={{
                        background: 'rgba(231, 76, 60, 0.2)',
                        border: '1px solid rgba(231, 76, 60, 0.4)',
                        color: '#e74c3c',
                        padding: '0.4rem 0.6rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        fontSize: '0.9rem',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          'rgba(231, 76, 60, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          'rgba(231, 76, 60, 0.2)';
                      }}
                    >
                      <FaTimes />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <Alert
            variant="danger"
            onClose={() => setError('')}
            dismissible
            style={{
              position: 'fixed',
              top: '80px',
              right: '20px',
              zIndex: 9999,
              minWidth: '300px',
              maxWidth: '400px',
              padding: '8px 12px',
              fontSize: '0.9rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            variant="success"
            onClose={() => setSuccess('')}
            dismissible
            style={{
              position: 'fixed',
              top: '80px',
              right: '20px',
              zIndex: 9999,
              minWidth: '300px',
              maxWidth: '400px',
              padding: '8px 12px',
              fontSize: '0.9rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            {success}
          </Alert>
        )}

        {/* Timeline View */}
        <div className="row" style={{ margin: 0 }}>
          <div className="col-12" style={{ padding: 0 }}>
            <Card
              className="shadow-sm border-0"
              style={{
                marginBottom: 0,
                height: isFullscreen ? 'calc(100vh - 90px)' : 'auto',
              }}
            >
              <Card.Body
                className="p-0"
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading scheduler...</p>
                  </div>
                ) : (
                  <div
                    className="scheduler-container"
                    onMouseDownCapture={handleMouseDown}
                    style={{
                      cursor: isPanning ? 'grabbing' : 'default',
                      flex: isFullscreen ? 1 : 'none',
                      overflow: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      maxHeight: isFullscreen ? 'none' : 'calc(100vh - 180px)',
                    }}
                  >
                    {/* Time scale header */}
                    <div className="timeline-header">
                      <div className="batch-label-header">Batches</div>
                      <div className="timeline-scale">
                        {Array.from({ length: hoursRange }, (_, i) => {
                          const hour = timelineStart + i;
                          return (
                            <div key={hour} className="time-marker">
                              {hour > 12 ? hour - 12 : hour}
                              {hour >= 12 ? 'PM' : 'AM'}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Batch tracks */}
                    <div
                      className="timeline-body"
                      style={{
                        position: 'relative',
                        minHeight: '200px',
                      }}
                    >
                      {/* Current Time Indicator */}
                      {isToday() &&
                        getCurrentTimePosition() !== null &&
                        (() => {
                          const batchTrackHeight = 60; // min-height from CSS
                          const totalHeight = batches.length * batchTrackHeight;

                          return (
                            <div
                              className="current-time-indicator"
                              style={{
                                position: 'absolute',
                                left: `${130 + getCurrentTimePosition()}px`,
                                top: 0,
                                height: `${totalHeight}px`,
                                width: '3px',
                                background:
                                  'linear-gradient(to bottom, #ff4444, #ff6666)',
                                zIndex: 50,
                                boxShadow: '0 0 8px rgba(255, 68, 68, 0.6)',
                                pointerEvents: 'none',
                              }}
                            >
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '3px',
                                  height: '100%',
                                }}
                              >
                                <div
                                  style={{
                                    position: 'sticky',
                                    top: '0',
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 0,
                                      height: 0,
                                      borderLeft: '6px solid transparent',
                                      borderRight: '6px solid transparent',
                                      borderTop: '8px solid #ff4444',
                                      marginLeft: '-4.5px',
                                    }}
                                  />
                                  <div
                                    style={{
                                      background: '#ff4444',
                                      color: 'white',
                                      padding: '3px 8px',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      whiteSpace: 'nowrap',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                      minWidth: '65px',
                                      textAlign: 'center',
                                      marginLeft: '-33.5px',
                                    }}
                                  >
                                    {currentTime.toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true,
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      {batches.map((batch) => (
                        <div
                          key={batch.id}
                          className="batch-track"
                          onDragOver={(e) => handleBatchDragOver(e, batch.id)}
                          onDrop={(e) => handleBatchDrop(e, batch.id)}
                          style={{
                            background:
                              dragOverBatchId === batch.id
                                ? '#eef4ff'
                                : undefined,
                            borderTop:
                              dragOverBatchId === batch.id
                                ? '2px solid #4c6ef5'
                                : undefined,
                          }}
                        >
                          <div
                            className="batch-label"
                            onContextMenu={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setContextMenu({
                                x: e.clientX,
                                y: e.clientY,
                                batch: batch,
                              });
                            }}
                            style={{ cursor: 'context-menu' }}
                          >
                            <button
                              type="button"
                              draggable
                              onDragStart={(e) =>
                                handleBatchDragStart(e, batch.id)
                              }
                              onDragEnd={handleBatchDragEnd}
                              onClick={(e) => e.stopPropagation()}
                              title="Drag to reorder batch row"
                              style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#94a3b8',
                                padding: '4px 6px 4px 0',
                                marginRight: '4px',
                                cursor: 'grab',
                                display: 'inline-flex',
                                alignItems: 'center',
                                alignSelf: 'stretch',
                              }}
                            >
                              <FaGripVertical />
                            </button>
                            <div>
                              <strong style={{ display: 'block' }}>
                                {batch.class_name} - {batch.division}
                              </strong>
                              <small
                                style={{
                                  display: 'block',
                                  color: batch.room_id ? '#666' : '#999',
                                  fontSize: '0.75rem',
                                  marginTop: '2px',
                                  fontStyle: batch.room_id
                                    ? 'normal'
                                    : 'italic',
                                }}
                              >
                                <FaDoorOpen
                                  style={{
                                    fontSize: '0.7rem',
                                    marginRight: '4px',
                                  }}
                                />
                                {batch.room_id
                                  ? rooms.find((r) => r.id === batch.room_id)
                                      ?.name || 'Unknown'
                                  : 'No room'}
                              </small>
                            </div>
                          </div>
                          <div
                            className="timeline-track"
                            onMouseDown={(e) =>
                              handleTrackMouseDown(e, batch.id)
                            }
                            onDoubleClick={(e) => handleTrackClick(e, batch.id)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, batch.id)}
                            onClick={(e) => {
                              // Deselect all if clicking on empty area
                              if (e.target.className === 'timeline-track') {
                                setSelectedSessions([]);
                              }
                            }}
                          >
                            {/* Grid lines */}
                            {Array.from({ length: hoursRange }, (_, i) => (
                              <div key={i} className="grid-line" />
                            ))}

                            {/* Sessions */}
                            {getBatchSessions(batch.id).map((session) => {
                              // Use resize preview if this session is being resized
                              const displaySession =
                                resizePreview && resizePreview.id === session.id
                                  ? {
                                      ...session,
                                      time_from: resizePreview.time_from,
                                      time_to: resizePreview.time_to,
                                    }
                                  : session;

                              const position = getSessionPosition(
                                displaySession.time_from,
                                displaySession.time_to,
                              );
                              const isLoading = loadingSessionIds.has(
                                session.id,
                              );
                              const isSelected = selectedSessions.includes(
                                session.id,
                              );
                              const blockStyle = getSessionBlockStyle(session);
                              const displayItems =
                                getSelectedSessionDisplayItems(session);
                              return (
                                <div
                                  key={session.id}
                                  className="session-block session-block-primary-only"
                                  draggable
                                  data-session-id={session.id}
                                  onDragStart={(e) =>
                                    handleDragStart(e, session)
                                  }
                                  onMouseEnter={(e) =>
                                    handleSessionMouseEnter(e, session)
                                  }
                                  onMouseLeave={handleSessionMouseLeave}
                                  style={{
                                    left: position.left,
                                    width: position.width,
                                    ...blockStyle,
                                    cursor: 'move',
                                    opacity: isLoading ? 0.5 : 1,
                                    transition: 'opacity 0.2s ease',
                                    border: isSelected
                                      ? '3px solid #ff6b35'
                                      : blockStyle.border,
                                    boxShadow: isSelected
                                      ? '0 0 12px rgba(255, 107, 53, 0.7)'
                                      : undefined,
                                  }}
                                  onClick={(e) => {
                                    if (e.ctrlKey || e.metaKey) {
                                      // Multi-select with Ctrl/Cmd
                                      e.stopPropagation();
                                      setSelectedSessions((prev) =>
                                        prev.includes(session.id)
                                          ? prev.filter(
                                              (id) => id !== session.id,
                                            )
                                          : [...prev, session.id],
                                      );
                                    } else {
                                      // Single select
                                      setSelectedSessions([session.id]);
                                    }
                                  }}
                                  onDoubleClick={() => handleView(session)}
                                  onContextMenu={(e) =>
                                    handleContextMenu(e, session)
                                  }
                                >
                                  {/* Left resize handle */}
                                  <div
                                    draggable={false}
                                    style={{
                                      position: 'absolute',
                                      left: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: '6px',
                                      cursor: 'ew-resize',
                                      background: 'rgba(0,0,0,0.1)',
                                      zIndex: 2,
                                    }}
                                    onMouseDown={(e) =>
                                      handleResizeStart(e, session, 'left')
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    onDragStart={(e) => e.preventDefault()}
                                  />

                                  {/* Right resize handle */}
                                  <div
                                    draggable={false}
                                    style={{
                                      position: 'absolute',
                                      right: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: '6px',
                                      cursor: 'ew-resize',
                                      background: 'rgba(0,0,0,0.1)',
                                      zIndex: 2,
                                    }}
                                    onMouseDown={(e) =>
                                      handleResizeStart(e, session, 'right')
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    onDragStart={(e) => e.preventDefault()}
                                  />

                                  <div className="session-content">
                                    <div
                                      className={`session-primary-wrap session-primary-wrap-count-${Math.min(displayItems.length, 4)}`}
                                    >
                                      {displayItems.map((item) => (
                                        <div
                                          key={item.key}
                                          className={`session-primary-label session-primary-label-count-${Math.min(displayItems.length, 4)}`}
                                          title={item.text}
                                          style={getSessionDisplayTextStyle(
                                            session,
                                            item.key,
                                          )}
                                        >
                                          {item.text}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            {/* New session preview */}
                            {newSessionPreview &&
                              newSessionPreview.batchId === batch.id &&
                              (() => {
                                const position = getSessionPosition(
                                  newSessionPreview.time_from,
                                  newSessionPreview.time_to,
                                );
                                return (
                                  <div
                                    className="session-block"
                                    style={{
                                      left: position.left,
                                      width: position.width,
                                      background: 'rgba(102, 126, 234, 0.3)',
                                      border: '2px dashed #667eea',
                                      opacity: 0.7,
                                      pointerEvents: 'none',
                                    }}
                                  >
                                    <div className="session-content">
                                      <div className="session-info">
                                        <div className="session-line">
                                          <FaClock className="session-icon-inline" />
                                          {formatTime12Hour(
                                            newSessionPreview.time_from.substring(
                                              0,
                                              5,
                                            ),
                                          )}{' '}
                                          -{' '}
                                          {formatTime12Hour(
                                            newSessionPreview.time_to.substring(
                                              0,
                                              5,
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>

      {/* Add/Edit Session Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        style={{ zIndex: 10000 }}
      >
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Modal.Title>
            {editingSession ? (
              <>
                <FaEdit className="me-2" />
                Edit Session
              </>
            ) : (
              <>
                <FaPlus className="me-2" />
                Add New Session
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="p-4">
            {/* Error/Success Messages in Modal */}
            {error && (
              <Alert
                variant="danger"
                onClose={() => setError('')}
                dismissible
                className="mb-3"
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                variant="success"
                onClose={() => setSuccess('')}
                dismissible
                className="mb-3"
              >
                {success}
              </Alert>
            )}

            {/* Basic Info - 3 columns */}
            <div className="row g-2 mb-3">
              {/* Batch Card */}
              <div className="col-md-4">
                <div
                  className="p-3 border rounded h-100"
                  style={{ background: '#f8f9fa' }}
                >
                  <div className="d-flex align-items-center mb-2">
                    <FaChalkboardTeacher className="text-primary me-2" />
                    <small className="text-muted fw-bold">BATCH</small>
                  </div>
                  <Form.Select
                    name="batch_id"
                    value={formData.batch_id}
                    onChange={handleInputChange}
                    required
                    size="sm"
                  >
                    <option value="">Select...</option>
                    {batches.map((batch) => (
                      <option key={batch.id} value={batch.id}>
                        {batch.class_name} - {batch.division}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              {/* Date Card */}
              <div className="col-md-4">
                <div
                  className="p-3 border rounded h-100"
                  style={{ background: '#f8f9fa' }}
                >
                  <div className="d-flex align-items-center mb-2">
                    <FaClock className="text-primary me-2" />
                    <small className="text-muted fw-bold">DATE</small>
                  </div>
                  <Form.Control
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    size="sm"
                  />
                </div>
              </div>

              {/* Session Type Card */}
              <div className="col-md-4">
                <div
                  className="p-3 border rounded h-100"
                  style={{ background: '#f8f9fa' }}
                >
                  <div className="d-flex align-items-center mb-2">
                    <FaBook className="text-primary me-2" />
                    <small className="text-muted fw-bold">TYPE</small>
                  </div>
                  <Form.Select
                    name="session_type"
                    value={formData.session_type}
                    onChange={handleInputChange}
                    required
                    size="sm"
                  >
                    <option value="class">Class</option>
                    <option value="exam">Exam</option>
                    <option value="break">Break</option>
                  </Form.Select>
                </div>
              </div>
            </div>

            {/* Visual Time Picker */}
            <div className="mb-4">
              <div className="d-flex align-items-center mb-3">
                <FaClock className="text-primary me-2" />
                <h6 className="mb-0 fw-bold">Schedule Time</h6>
              </div>

              <Card
                className="border-0 shadow-sm"
                style={{
                  background:
                    'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                }}
              >
                <Card.Body className="p-3">
                  {/* Visual Timeline Selector */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <small className="text-muted">6 AM</small>
                      <small className="text-muted">10 PM</small>
                    </div>
                    <div
                      style={{
                        position: 'relative',
                        height: '60px',
                        background: 'white',
                        borderRadius: '8px',
                        border: '2px solid #dee2e6',
                      }}
                    >
                      {/* Timeline markers */}
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                        }}
                      >
                        {Array.from({ length: 17 }, (_, i) => (
                          <div
                            key={i}
                            style={{
                              flex: 1,
                              borderRight:
                                i < 16 ? '1px solid #e9ecef' : 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              color: '#adb5bd',
                            }}
                          >
                            {i % 2 === 0 && 6 + i}
                          </div>
                        ))}
                      </div>

                      {/* Selected time range visualization */}
                      {formData.time_from &&
                        formData.time_to &&
                        (() => {
                          const fromParts = formData.time_from.split(':');
                          const toParts = formData.time_to.split(':');
                          const fromHour =
                            parseInt(fromParts[0]) +
                            parseInt(fromParts[1]) / 60;
                          const toHour =
                            parseInt(toParts[0]) + parseInt(toParts[1]) / 60;
                          const left = ((fromHour - 6) / 16) * 100;
                          const width = ((toHour - fromHour) / 16) * 100;

                          return (
                            <div
                              style={{
                                position: 'absolute',
                                left: `${left}%`,
                                width: `${width}%`,
                                top: '5px',
                                bottom: '5px',
                                background:
                                  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '6px',
                                opacity: 0.8,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                lineHeight: '1.2',
                                padding: '2px',
                              }}
                            >
                              <div>{formData.time_from.substring(0, 5)}</div>
                              <div>{formData.time_to.substring(0, 5)}</div>
                            </div>
                          );
                        })()}
                    </div>
                  </div>

                  {/* Quick Time Buttons */}
                  <div className="mb-3">
                    <small className="text-muted d-block mb-2">
                      Quick Select Duration:
                    </small>
                    <div className="d-flex gap-2 flex-wrap">
                      {[
                        { label: '30 min', duration: 30 },
                        { label: '45 min', duration: 45 },
                        { label: '1 hour', duration: 60 },
                        { label: '1.5 hours', duration: 90 },
                        { label: '2 hours', duration: 120 },
                      ].map(({ label, duration }) => (
                        <Button
                          key={duration}
                          size="sm"
                          variant="outline-primary"
                          onClick={() => {
                            if (formData.time_from) {
                              const [hours, minutes] =
                                formData.time_from.split(':');
                              const totalMinutes =
                                parseInt(hours) * 60 +
                                parseInt(minutes) +
                                duration;
                              const newHours = Math.floor(totalMinutes / 60);
                              const newMinutes = totalMinutes % 60;
                              setFormData((prev) => ({
                                ...prev,
                                time_to: `${String(newHours).padStart(
                                  2,
                                  '0',
                                )}:${String(newMinutes).padStart(2, '0')}`,
                              }));
                            }
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Time Input Fields */}
                  <div className="row g-2">
                    <div className="col-6">
                      <Form.Label className="small text-muted mb-1">
                        Start Time
                      </Form.Label>
                      <Form.Control
                        type="time"
                        name="time_from"
                        value={formData.time_from}
                        onChange={handleInputChange}
                        required
                        size="sm"
                      />
                    </div>
                    <div className="col-6">
                      <Form.Label className="small text-muted mb-1">
                        End Time
                      </Form.Label>
                      <Form.Control
                        type="time"
                        name="time_to"
                        value={formData.time_to}
                        onChange={handleInputChange}
                        required
                        size="sm"
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>

            {/* Academic & Location - 3 columns */}
            <div className="row g-2 mb-3">
              {formData.session_type === 'class' && (
                <>
                  <div className="col-md-4">
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body className="p-2">
                        <small className="text-muted d-block mb-1 fw-bold">
                          <FaBook className="me-1" />
                          SUBJECT
                        </small>
                        <Form.Select
                          name="subject_id"
                          value={formData.subject_id}
                          onChange={handleInputChange}
                          size="sm"
                        >
                          <option value="">Select Subject</option>
                          {subjects.map((subject) => (
                            <option key={subject.id} value={subject.id}>
                              {subject.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Card.Body>
                    </Card>
                  </div>

                  <div className="col-md-4">
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body className="p-2">
                        <small className="text-muted d-block mb-1 fw-bold">
                          <FaChalkboardTeacher className="me-1" />
                          TEACHER
                        </small>
                        <Form.Select
                          name="teacher_id"
                          value={formData.teacher_id}
                          onChange={handleInputChange}
                          size="sm"
                        >
                          <option value="">Select Teacher</option>
                          {teachers.map((teacher) => (
                            <option key={teacher.id} value={teacher.id}>
                              {teacher.name} ({teacher.staff_code})
                            </option>
                          ))}
                        </Form.Select>
                      </Card.Body>
                    </Card>
                  </div>
                </>
              )}

              <div
                className={
                  formData.session_type === 'class' ? 'col-md-4' : 'col-md-6'
                }
              >
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="p-2">
                    <small className="text-muted d-block mb-1 fw-bold">
                      <FaDoorOpen className="me-1" />
                      ROOM
                    </small>
                    <Form.Select
                      name="room_id"
                      value={formData.room_id}
                      onChange={handleInputChange}
                      size="sm"
                    >
                      <option value="">Select Room</option>
                      {rooms.map((room) => (
                        <option key={room.id} value={room.id}>
                          {room.name} - {room.location}
                        </option>
                      ))}
                    </Form.Select>
                  </Card.Body>
                </Card>
              </div>
            </div>

            {/* Notes - Full width */}
            <div className="mb-2">
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-2">
                  <small className="text-muted d-block mb-1 fw-bold">
                    NOTES
                  </small>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Add any additional notes..."
                    style={{ resize: 'vertical', fontSize: '0.85rem' }}
                    size="sm"
                  />
                </Card.Body>
              </Card>
            </div>
          </Modal.Body>
          <Modal.Footer>
            {editingSession && (
              <Button
                variant="outline-danger"
                onClick={() => {
                  handleCloseModal();
                  handleDelete(editingSession);
                }}
                disabled={submitting}
              >
                <FaTrash className="me-2" />
                Delete
              </Button>
            )}
            <div className="ms-auto">
              <Button
                variant="secondary"
                onClick={handleCloseModal}
                disabled={submitting}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    {editingSession ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    {editingSession ? (
                      <>
                        <FaEdit className="me-2" />
                        Update Session
                      </>
                    ) : (
                      <>
                        <FaPlus className="me-2" />
                        Create Session
                      </>
                    )}
                  </>
                )}
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Copy Schedule Modal */}
      <Modal
        show={showCopyModal}
        onHide={() => setShowCopyModal(false)}
        onShow={() => checkTargetDateSessions(copyTargetDate)}
        centered
        style={{ zIndex: 10000 }}
      >
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Modal.Title>Copy Schedule to Another Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Copy all <strong>{sessions.length} sessions</strong> from{' '}
            <strong>{selectedDate}</strong> to a new date.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Target Date</Form.Label>
            <Form.Control
              type="date"
              value={copyTargetDate}
              onChange={(e) => {
                setCopyTargetDate(e.target.value);
                checkTargetDateSessions(e.target.value);
              }}
            />
          </Form.Group>
          {copyTargetDate === selectedDate && (
            <small className="text-danger d-block mb-2">
              Please select a different date
            </small>
          )}
          {targetDateSessions && targetDateSessions.length > 0 && (
            <div className="alert alert-warning mb-3">
              <strong>Warning:</strong> Target date already has{' '}
              <strong>{targetDateSessions.length} session(s)</strong>.
              <Form.Group className="mt-2">
                <Form.Check
                  type="radio"
                  id="merge-mode"
                  label="Merge - Add new sessions alongside existing ones"
                  name="copyMode"
                  value="merge"
                  checked={copyMode === 'merge'}
                  onChange={(e) => setCopyMode(e.target.value)}
                />
                <Form.Check
                  type="radio"
                  id="replace-mode"
                  label={`Replace - Delete all ${targetDateSessions.length} existing session(s) first`}
                  name="copyMode"
                  value="replace"
                  checked={copyMode === 'replace'}
                  onChange={(e) => setCopyMode(e.target.value)}
                  className="text-danger"
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCopyModal(false)}
            disabled={copying}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCopySchedule}
            disabled={copying || copyTargetDate === selectedDate}
          >
            {copying ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {copyMode === 'replace' ? 'Replacing...' : 'Copying...'}
              </>
            ) : (
              <>
                {copyMode === 'replace' && targetDateSessions?.length > 0
                  ? 'Replace & Copy'
                  : 'Copy Schedule'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <input
        ref={teacherColorInputRef}
        type="color"
        style={{ position: 'fixed', opacity: 0, pointerEvents: 'none' }}
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleTeacherColorInputChange}
      />

      {/* View Session Modal */}
      <Modal
        show={showViewModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        style={{ zIndex: 10000 }}
      >
        <Modal.Header
          closeButton
          className="py-2"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Modal.Title
            className="d-flex align-items-center"
            style={{ fontSize: '1.1rem' }}
          >
            <FaClock className="me-2" />
            Session Details
            {viewSession && (
              <Badge
                bg={
                  viewSession.session_type === 'class'
                    ? 'light'
                    : viewSession.session_type === 'exam'
                      ? 'warning'
                      : 'info'
                }
                text="dark"
                className="ms-2"
                style={{ fontSize: '0.7rem', padding: '4px 10px' }}
              >
                {viewSession.session_type.toUpperCase()}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          {viewSession && (
            <div className="row g-2">
              {/* Time & Date - Most Important */}
              <div className="col-12">
                <div
                  className="p-2 rounded"
                  style={{
                    background:
                      'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                  }}
                >
                  <div className="row g-2">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <FaClock className="me-2 text-primary" />
                        <div>
                          <small
                            className="text-muted d-block"
                            style={{ fontSize: '0.7rem', lineHeight: 1 }}
                          >
                            TIME
                          </small>
                          <strong style={{ fontSize: '0.95rem' }}>
                            {viewSession.time_from.substring(0, 5)} -{' '}
                            {viewSession.time_to.substring(0, 5)}
                          </strong>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center">
                        <FaClock
                          className="me-2 text-primary"
                          style={{ opacity: 0.6 }}
                        />
                        <div>
                          <small
                            className="text-muted d-block"
                            style={{ fontSize: '0.7rem', lineHeight: 1 }}
                          >
                            DATE
                          </small>
                          <strong style={{ fontSize: '0.9rem' }}>
                            {new Date(viewSession.date).toLocaleDateString(
                              'en-US',
                              {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              },
                            )}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Batch */}
              <div className="col-md-6">
                <div className="p-2 border rounded h-100">
                  <small
                    className="text-muted d-block mb-1"
                    style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                  >
                    <FaChalkboardTeacher className="me-1" />
                    BATCH
                  </small>
                  <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                    {
                      batches.find((b) => b.id === viewSession.batch_id)
                        ?.class_name
                    }{' '}
                    -{' '}
                    {
                      batches.find((b) => b.id === viewSession.batch_id)
                        ?.division
                    }
                  </div>
                </div>
              </div>

              {/* Session Type */}
              <div className="col-md-6">
                <div className="p-2 border rounded h-100">
                  <small
                    className="text-muted d-block mb-1"
                    style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                  >
                    TYPE
                  </small>
                  <Badge
                    bg={
                      viewSession.session_type === 'class'
                        ? 'primary'
                        : viewSession.session_type === 'exam'
                          ? 'warning'
                          : 'info'
                    }
                    style={{ fontSize: '0.8rem', padding: '4px 12px' }}
                  >
                    {viewSession.session_type.toUpperCase()}
                  </Badge>
                </div>
              </div>

              {/* Teacher - Always show even if empty */}
              <div className="col-md-6">
                <div className="p-2 border rounded h-100">
                  <small
                    className="text-muted d-block mb-1"
                    style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                  >
                    <FaChalkboardTeacher className="me-1" />
                    TEACHER
                  </small>
                  {viewSession.teacher_name ? (
                    <>
                      <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                        {viewSession.teacher_name}
                      </div>
                      <small
                        className="text-muted"
                        style={{ fontSize: '0.75rem' }}
                      >
                        {viewSession.teacher_code}
                      </small>
                    </>
                  ) : (
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Not assigned
                    </div>
                  )}
                </div>
              </div>

              {/* Subject - Always show even if empty */}
              <div className="col-md-6">
                <div className="p-2 border rounded h-100">
                  <small
                    className="text-muted d-block mb-1"
                    style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                  >
                    <FaBook className="me-1" />
                    SUBJECT
                  </small>
                  {viewSession.subject_name ? (
                    <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                      {viewSession.subject_name}
                    </div>
                  ) : (
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Not assigned
                    </div>
                  )}
                </div>
              </div>

              {/* Room - Always show even if empty */}
              <div className="col-md-6">
                <div className="p-2 border rounded h-100">
                  <small
                    className="text-muted d-block mb-1"
                    style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                  >
                    <FaDoorOpen className="me-1" />
                    ROOM
                  </small>
                  {viewSession.room_name ? (
                    <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                      {viewSession.room_name}
                    </div>
                  ) : (
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Not assigned
                    </div>
                  )}
                </div>
              </div>

              {/* Notes - Always show even if empty */}
              <div className="col-12">
                <div
                  className="p-2 border rounded"
                  style={{ background: '#f8f9fa' }}
                >
                  <small
                    className="text-muted d-block mb-1"
                    style={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                  >
                    NOTES
                  </small>
                  {viewSession.notes ? (
                    <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                      {viewSession.notes}
                    </div>
                  ) : (
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                      No notes
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between py-2">
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => {
              handleCloseModal();
              handleDelete(viewSession);
            }}
          >
            <FaTrash className="me-1" />
            Delete
          </Button>
          <div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleEdit(viewSession)}
              className="me-2"
            >
              <FaEdit className="me-1" />
              Edit
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleCloseModal}
            >
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Context Menu */}
      {hoveredSessionTooltip && (
        <div
          style={{
            position: 'fixed',
            top: hoveredSessionTooltip.top,
            left: hoveredSessionTooltip.left,
            transform: 'translateX(-50%)',
            background: 'rgba(20, 24, 38, 0.96)',
            color: '#fff',
            padding: '10px 12px',
            borderRadius: '8px',
            boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
            zIndex: 10001,
            minWidth: '220px',
            maxWidth: '320px',
            pointerEvents: 'none',
            fontSize: '0.78rem',
            lineHeight: 1.45,
          }}
          onMouseLeave={hideSessionTooltip}
        >
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderBottom: '8px solid rgba(20, 24, 38, 0.96)',
            }}
          />
          <div
            style={{
              fontWeight: 700,
              fontSize: '0.84rem',
              marginBottom: '4px',
            }}
          >
            {hoveredSessionTooltip.title}
          </div>
          <div>{hoveredSessionTooltip.timeLabel}</div>
          {hoveredSessionTooltip.roomName && (
            <div>Room: {hoveredSessionTooltip.roomName}</div>
          )}
          {hoveredSessionTooltip.notes && (
            <div>Notes: {hoveredSessionTooltip.notes}</div>
          )}
        </div>
      )}

      {contextMenu &&
        (() => {
          // Calculate if menu should open upward or to the left
          const menuEstimatedHeight = 400; // Estimated max height
          const menuEstimatedWidth = 200; // Estimated width
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;

          const shouldOpenUp =
            contextMenu.y + menuEstimatedHeight > viewportHeight;
          const shouldOpenLeft =
            contextMenu.x + menuEstimatedWidth > viewportWidth;

          // Calculate max height based on available space
          const maxHeight = shouldOpenUp
            ? contextMenu.y - 20 // Space from top
            : viewportHeight - contextMenu.y - 20; // Space from bottom

          return (
            <div
              style={{
                position: 'fixed',
                ...(shouldOpenUp
                  ? { bottom: viewportHeight - contextMenu.y }
                  : { top: contextMenu.y }),
                ...(shouldOpenLeft
                  ? { right: viewportWidth - contextMenu.x }
                  : { left: contextMenu.x }),
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 10000,
                minWidth: '150px',
                maxHeight: `${maxHeight}px`,
                overflowY: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Batch Context Menu */}
              {contextMenu.batch && !contextMenu.mode && (
                <div
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = '#f8f9fa')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = 'white')
                  }
                  onClick={() => {
                    setContextMenu({
                      x: contextMenu.x,
                      y: contextMenu.y,
                      batch: contextMenu.batch,
                      mode: 'batch-room',
                    });
                  }}
                >
                  <FaDoorOpen style={{ marginRight: '8px' }} />
                  Assign Room →
                </div>
              )}

              {/* Batch Room Selection */}
              {contextMenu.mode === 'batch-room' && (
                <>
                  <div
                    style={{
                      padding: '8px 16px',
                      borderBottom: '1px solid #eee',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                    }}
                  >
                    Select Room
                  </div>
                  <div
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = '#f8f9fa')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'white')
                    }
                    onClick={() => {
                      setContextMenu(null);
                      handleBatchRoomUpdate(contextMenu.batch, null);
                    }}
                  >
                    <span style={{ fontStyle: 'italic', color: '#999' }}>
                      None
                    </span>
                    {!contextMenu.batch.room_id && <span>✓</span>}
                  </div>
                  {rooms.map((room) => (
                    <div
                      key={room.id}
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'white')
                      }
                      onClick={() => {
                        setContextMenu(null);
                        handleBatchRoomUpdate(contextMenu.batch, room.id);
                      }}
                    >
                      <span>{room.name}</span>
                      {contextMenu.batch.room_id === room.id && <span>✓</span>}
                    </div>
                  ))}
                </>
              )}

              {/* Create Session - Subject Selection */}
              {contextMenu.mode === 'create-subject' && (
                <>
                  <div
                    style={{
                      padding: '8px 16px',
                      borderBottom: '1px solid #eee',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                    }}
                  >
                    Select Subject (Optional)
                  </div>
                  {/* Skip option */}
                  <div
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      color: '#6c757d',
                      fontStyle: 'italic',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = '#f8f9fa')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'white')
                    }
                    onClick={() => {
                      // Skip subject selection, go to teacher selection
                      setContextMenu({
                        x: contextMenu.x,
                        y: contextMenu.y,
                        mode: 'create-teacher',
                        selectedSubject: null,
                        sessionType: contextMenu.sessionType,
                      });
                    }}
                  >
                    Skip (No Subject)
                  </div>
                  {subjects.map((subject) => (
                    <div
                      key={subject.id}
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'white')
                      }
                      onClick={() => {
                        setContextMenu({
                          x: contextMenu.x,
                          y: contextMenu.y,
                          mode: 'create-teacher',
                          selectedSubject: subject.id,
                          sessionType: contextMenu.sessionType,
                        });
                      }}
                    >
                      <FaBook style={{ marginRight: '8px' }} />
                      {subject.name}
                    </div>
                  ))}
                </>
              )}

              {/* Create Session - Teacher Selection */}
              {contextMenu.mode === 'create-teacher' && (
                <>
                  <div
                    style={{
                      padding: '8px 16px',
                      borderBottom: '1px solid #eee',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                    }}
                  >
                    Select Teacher (Optional)
                  </div>
                  {/* Skip option */}
                  <div
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      color: '#6c757d',
                      fontStyle: 'italic',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = '#f8f9fa')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'white')
                    }
                    onClick={() => {
                      // Create with subject but no teacher
                      handleCreateWithSubjectTeacher(
                        contextMenu.selectedSubject,
                        null,
                        contextMenu.sessionType,
                      );
                    }}
                  >
                    Skip (No Teacher)
                  </div>
                  {teachers.map((teacher) => (
                    <div
                      key={teacher.id}
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'white')
                      }
                      onClick={() => {
                        handleCreateWithSubjectTeacher(
                          contextMenu.selectedSubject,
                          teacher.id,
                          contextMenu.sessionType,
                        );
                      }}
                    >
                      <FaChalkboardTeacher style={{ marginRight: '8px' }} />
                      {teacher.name}
                    </div>
                  ))}
                </>
              )}

              {/* Session Context Menu */}
              {contextMenu.session && !contextMenu.mode && (
                <>
                  <div
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = '#f8f9fa')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'white')
                    }
                    onClick={() => {
                      setContextMenu(null);
                      handleView(contextMenu.session);
                    }}
                  >
                    <FaEdit style={{ marginRight: '8px' }} />
                    View Details
                  </div>

                  {/* Type Menu Item */}
                  <div
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = '#f8f9fa')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'white')
                    }
                    onClick={() => {
                      setContextMenu({
                        x: contextMenu.x,
                        y: contextMenu.y,
                        session: contextMenu.session,
                        mode: 'select-type',
                      });
                    }}
                  >
                    <FaClock style={{ marginRight: '8px' }} />
                    Type →
                  </div>

                  {/* Subject Menu Item */}
                  {contextMenu.session.session_type === 'class' && (
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'white')
                      }
                      onClick={() => {
                        setContextMenu({
                          x: contextMenu.x,
                          y: contextMenu.y,
                          session: contextMenu.session,
                          mode: 'select-subject',
                        });
                      }}
                    >
                      <FaBook style={{ marginRight: '8px' }} />
                      Subject →
                    </div>
                  )}

                  {/* Teacher Menu Item */}
                  {contextMenu.session.session_type === 'class' && (
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'white')
                      }
                      onClick={() => {
                        setContextMenu({
                          x: contextMenu.x,
                          y: contextMenu.y,
                          session: contextMenu.session,
                          mode: 'select-teacher',
                        });
                      }}
                    >
                      <FaChalkboardTeacher style={{ marginRight: '8px' }} />
                      Teacher →
                    </div>
                  )}

                  {/* Room Menu Item */}
                  <div
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = '#f8f9fa')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'white')
                    }
                    onClick={() => {
                      setContextMenu({
                        x: contextMenu.x,
                        y: contextMenu.y,
                        session: contextMenu.session,
                        mode: 'select-room',
                      });
                    }}
                  >
                    <FaDoorOpen style={{ marginRight: '8px' }} />
                    Room →
                  </div>

                  {contextMenu.session.session_type === 'class' &&
                    contextMenu.session.teacher_id && (
                      <div
                        style={{
                          padding: '8px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = '#f8f9fa')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = 'white')
                        }
                        onClick={() => {
                          const teacherId = contextMenu.session.teacher_id;
                          setContextMenu(null);
                          openTeacherColorPicker(teacherId);
                        }}
                      >
                        <FaChalkboardTeacher style={{ marginRight: '8px' }} />
                        Teacher Color...
                      </div>
                    )}

                  {contextMenu.session.session_type === 'class' &&
                    contextMenu.session.teacher_id &&
                    teacherColorOverrides[
                      String(contextMenu.session.teacher_id)
                    ] && (
                      <div
                        style={{
                          padding: '8px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = '#f8f9fa')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = 'white')
                        }
                        onClick={() => {
                          const teacherId = contextMenu.session.teacher_id;
                          setContextMenu(null);
                          handleTeacherColorReset(teacherId);
                        }}
                      >
                        <FaTimes style={{ marginRight: '8px' }} />
                        Reset Teacher Color
                      </div>
                    )}

                  <div
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      color: '#dc3545',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = '#f8f9fa')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'white')
                    }
                    onClick={() => {
                      setContextMenu(null);
                      handleDelete(contextMenu.session);
                    }}
                  >
                    <FaTrash style={{ marginRight: '8px' }} />
                    Delete
                  </div>
                </>
              )}

              {/* Select Type Mode */}
              {contextMenu.mode === 'select-type' && (
                <>
                  <div
                    style={{
                      padding: '8px 16px',
                      borderBottom: '1px solid #eee',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                    }}
                  >
                    Select Type
                  </div>
                  {['class', 'exam', 'break'].map((type) => (
                    <div
                      key={type}
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'white')
                      }
                      onClick={() => {
                        // Check if we're creating a new session or updating existing
                        if (contextMenu.session) {
                          // Update existing session
                          handleQuickUpdate(
                            contextMenu.session,
                            'session_type',
                            type,
                          );
                        } else {
                          // Creating new session - proceed based on type
                          if (type === 'break') {
                            // For break, create directly with default values
                            handleCreateWithType(type);
                          } else {
                            // For class and exam, show subject selection
                            setContextMenu({
                              x: contextMenu.x,
                              y: contextMenu.y,
                              mode: 'create-subject',
                              sessionType: type, // Store the type
                            });
                          }
                        }
                      }}
                    >
                      <span style={{ textTransform: 'capitalize' }}>
                        {type}
                      </span>
                      {contextMenu.session &&
                        contextMenu.session.session_type === type && (
                          <span>✓</span>
                        )}
                    </div>
                  ))}
                </>
              )}

              {/* Select Subject Mode */}
              {contextMenu.mode === 'select-subject' && (
                <>
                  <div
                    style={{
                      padding: '8px 16px',
                      borderBottom: '1px solid #eee',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                    }}
                  >
                    Select Subject
                  </div>
                  {subjects.map((subject) => (
                    <div
                      key={subject.id}
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'white')
                      }
                      onClick={() =>
                        handleQuickUpdate(
                          contextMenu.session,
                          'subject_id',
                          subject.id,
                        )
                      }
                    >
                      <span>{subject.name}</span>
                      {contextMenu.session.subject_id === subject.id && (
                        <span>✓</span>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* Select Teacher Mode */}
              {contextMenu.mode === 'select-teacher' && (
                <>
                  <div
                    style={{
                      padding: '8px 16px',
                      borderBottom: '1px solid #eee',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                    }}
                  >
                    Select Teacher
                  </div>
                  {teachers.map((teacher) => (
                    <div
                      key={teacher.id}
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'white')
                      }
                      onClick={() =>
                        handleQuickUpdate(
                          contextMenu.session,
                          'teacher_id',
                          teacher.id,
                        )
                      }
                    >
                      <span>{teacher.name}</span>
                      {contextMenu.session.teacher_id === teacher.id && (
                        <span>✓</span>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* Select Room Mode */}
              {contextMenu.mode === 'select-room' && (
                <>
                  <div
                    style={{
                      padding: '8px 16px',
                      borderBottom: '1px solid #eee',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                    }}
                  >
                    Select Room
                  </div>
                  {rooms.map((room) => (
                    <div
                      key={room.id}
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'white')
                      }
                      onClick={() =>
                        handleQuickUpdate(
                          contextMenu.session,
                          'room_id',
                          room.id,
                        )
                      }
                    >
                      <span>{room.name}</span>
                      {contextMenu.session.room_id === room.id && (
                        <span>✓</span>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          );
        })()}
    </Layout>
  );
};

export default ClassScheduler;
